import { useEffect, useRef, useState } from "react";

import { SortOrder } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { DateField, Loader, TableV2 as Table, Typography } from "@eisox/design-system";
import { useInfiniteQuery, type UseInfiniteQueryOptions } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";

import { m } from "~/paraglide/messages";

import { getValveHistoryMinDate } from "../helpers/valve-history.helper";

const ROW_HEIGHT_PX = 33;
const PAGE_SIZE = 50;

interface ValveHistoryPage<TEntry> {
  data: TEntry[];
  nextCursor?: string;
}

export interface ValveHistoryColumn<TEntry> {
  id: string;
  header: string;
  widthPercent: number;
  cell: (entry: TEntry) => React.ReactNode;
}

interface ValveHistoryTableProps<TEntry> {
  columns: ValveHistoryColumn<TEntry>[];
  query: (filters: {
    endDate: string;
    order: SortOrder;
    limit: number;
  }) => Pick<UseInfiniteQueryOptions<ValveHistoryPage<TEntry>>, "queryKey" | "queryFn">;
}

export const ValveHistoryTable = <TEntry,>({ columns, query }: ValveHistoryTableProps<TEntry>) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fromDate, setFromDate] = useState(() => dayjs().startOf("day"));

  const { data, isPending, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery<
    ValveHistoryPage<TEntry>
  >({
    ...query({ endDate: fromDate.endOf("day").toISOString(), order: SortOrder.DESC, limit: PAGE_SIZE }),
    initialPageParam: undefined,
    getNextPageParam: lastPage => lastPage.nextCursor,
  });

  const entries = data?.pages.flatMap(page => page.data) ?? [];

  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT_PX,
    overscan: 10,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const lastRenderedIndex = virtualRows.at(-1)?.index;
  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom = virtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0);

  useEffect(() => {
    if (lastRenderedIndex === undefined || lastRenderedIndex < entries.length - 1) return;
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [lastRenderedIndex, entries.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <>
      <div className="flex w-full flex-col items-start gap-[5px]">
        <Typography className="text-primary-dark-gray">{m.from()}</Typography>
        <DateField.Root
          value={fromDate}
          onValueChange={date => {
            if (date) setFromDate(date.startOf("day"));
          }}
          min={getValveHistoryMinDate()}
          max={dayjs()}
          className="w-fit"
        >
          <DateField.Input>
            <DateField.Trigger />
          </DateField.Input>
          <DateField.Content className="z-1500 w-min select-none">
            <DateField.Calendar />
          </DateField.Content>
        </DateField.Root>
      </div>
      <Table.Container ref={scrollRef} className="min-h-0 flex-1">
        <Table.Root className="table-fixed">
          <colgroup>
            {columns.map(column => (
              <col key={column.id} style={{ width: `${column.widthPercent}%` }} />
            ))}
          </colgroup>
          <Table.Header className="top-0 sticky z-10 bg-primary-white">
            <Table.Row>
              {columns.map(column => (
                <Table.Head key={column.id} className="font-bold py-[5px] text-left">
                  <Typography keepHoverOpen>{column.header}</Typography>
                </Table.Head>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {entries.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <div className="py-10 flex items-center justify-center text-primary-dark-gray">
                    {isPending ? (
                      <Loader size={24} />
                    ) : (
                      <Typography>{isError ? m.error_unknown() : m.no_historic()}</Typography>
                    )}
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : (
              <>
                {paddingTop > 0 && <tr style={{ height: paddingTop }} />}
                {virtualRows.map(virtualRow => {
                  const entry = entries[virtualRow.index];
                  if (entry === undefined) return null;
                  return (
                    <Table.Row key={virtualRow.key} style={{ height: virtualRow.size }}>
                      {columns.map(column => (
                        <Table.Cell key={column.id} className="truncate py-[5px] text-left">
                          <Typography keepHoverOpen>{column.cell(entry)}</Typography>
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  );
                })}
                {paddingBottom > 0 && <tr style={{ height: paddingBottom }} />}
              </>
            )}
            {isFetchingNextPage && (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <div className="py-3 flex items-center justify-center">
                    <Loader size={20} />
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Table.Container>
    </>
  );
};
